'use client';

import { toast } from 'src/components/snackbar';

import { registreraFel } from './felbutik';
import { begärRapportdialog } from './rapportdialog';

/**
 * Gemensam felvisning.
 *
 * Idag toastar varje anropsställe sin egen sträng med sin egen reservtext ("Ett fel uppstod",
 * "Något gick fel", "Ett oväntat fel uppstod"). Den här hjälparen finns för att de stegvis ska
 * kunna samlas på ett ställe — den migreringen ingår inte i den här ändringen, men helt nya
 * anropsställen bör använda `visaFel` direkt.
 */

/** Markering på felobjektet så att interceptorn och anropsstället inte dubbeltoastar. */
const HANTERAT = '__felVisat';

/** Gemensamt toast-id för allt som betyder "servern svarar inte" — en orsak, en toast. */
const SERVERN_SVARAR_INTE = 'fel-servern-svarar-inte';

export function ärRedanVisat(fel: any): boolean {
  return Boolean(fel?.[HANTERAT]);
}

export function markeraSomVisat(fel: any): void {
  try {
    if (fel && typeof fel === 'object') {
      Object.defineProperty(fel, HANTERAT, { value: true, enumerable: false });
    }
  } catch {
    // Frusna objekt går inte att märka. Värsta utfallet är en toast för mycket.
  }
}

/** Sant för ett fel som kommer ur ett HTTP-anrop, oavsett om svaret nådde fram. */
function ärHttpFel(fel: any): boolean {
  return Boolean(fel?.isAxiosError || fel?.response || fel?.config);
}

/**
 * Sant när servern svarade med vår egen felkropp (`ApiErrorResponse`).
 *
 * <p>Ett 500 utan den har aldrig varit hos `GlobalExceptionHandler`: det kommer från Next:s
 * proxy när backend är nere (kroppen är då strängen "Internal Server Error"), eller från ett
 * servletfilter, som ligger före `@ControllerAdvice`. Skillnaden är värd att visa för
 * användaren — ett fel med kropp har en referens att rapportera, ett utan har ingenting.
 */
export function harServermeddelande(fel: any): boolean {
  return typeof fel?.response?.data?.message === 'string' && fel.response.data.message.length > 0;
}

/**
 * Sant när anropet inte fick något begripligt svar alls: ingen kontakt, eller ett serverfel som
 * aldrig gick genom felhanteraren. Orsaken är densamma för varje samtidigt anrop, och
 * meddelandet är därför detsamma — se id:t i {@link visaFel}.
 */
export function serverSvaradeInte(fel: any): boolean {
  if (!ärHttpFel(fel)) {
    return false;
  }
  const status = fel?.response?.status;
  if (typeof status !== 'number') {
    return fel?.code !== 'ERR_CANCELED';
  }
  return status >= 500 && !harServermeddelande(fel);
}

export function meddelandeUrFel(fel: any, reservtext = 'Något gick fel'): string {
  if (harServermeddelande(fel)) {
    return fel.response.data.message;
  }
  // `fel.message` på ett axios-fel är teknisk engelska — "Request failed with status code 500" —
  // och det var precis den texten användaren fick i stället för reservtexten så länge den stod
  // först. Den duger bara för fel som inte kommer ur ett HTTP-anrop, där den är den enda text
  // som finns.
  if (ärHttpFel(fel)) {
    return reservtext;
  }
  return fel?.message || reservtext;
}

export function referensUrFel(fel: any): string | undefined {
  return (
    fel?.response?.data?.felReferens ||
    fel?.response?.headers?.['x-fel-referens'] ||
    undefined
  );
}

/**
 * Visar ett fel för användaren och erbjuder rapportering när det finns en referens att
 * rapportera på. Utan referens finns inget att felsöka och knappen vore ett tomt löfte.
 */
export function visaFel(fel: any, reservtext?: string): void {
  if (ärRedanVisat(fel)) {
    return;
  }
  markeraSomVisat(fel);

  const meddelande = meddelandeUrFel(fel, reservtext);
  const referens = referensUrFel(fel);

  registreraFel({
    referens,
    meddelande,
    tidpunkt: new Date(),
    sökväg: typeof window !== 'undefined' ? window.location.pathname : undefined,
    anrop: fel?.config?.url,
    status: fel?.response?.status,
  });

  toast.error(meddelande, {
    // Stabilt id: en anropsstorm mot samma trasiga endpoint kollapsar till en toast. När servern
    // inte svarar alls är orsaken gemensam för alla anrop — en sida som hämtar från fem ställen
    // gav annars fem likadana toastar ovanpå varandra, vilket är vad som faktiskt hände när
    // backend låg nere.
    id: referens ?? (serverSvaradeInte(fel) ? SERVERN_SVARAR_INTE : `fel-${fel?.config?.url ?? 'okänt'}`),
    duration: 8000,
    ...(referens
      ? {
          action: {
            label: 'Rapportera',
            onClick: () => begärRapportdialog(),
          },
        }
      : {}),
  });
}
